'use server';

import { fetchAuth } from '@/lib/fetch-auth';
import type {
  BillingInterval,
  Entitlements,
  Invoice,
  PaymentMethod,
  PlanCatalog,
  PlanId,
  UsageSummary,
} from './types';

/**
 * Server actions for billing.
 *
 * Every plan number the UI renders comes from the backend catalog, so pricing
 * is never duplicated in the frontend.
 */

/**
 * The public plan catalog. Unauthenticated, so the marketing pricing page can
 * render it without a session. Revalidated every five minutes.
 */
export async function getPlanCatalog(): Promise<PlanCatalog> {
  const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || '';
  const response = await fetch(`${baseUrl.replace(/\/$/, '')}/stripe/plans`, {
    next: { revalidate: 300 },
  });

  if (!response.ok) {
    throw new Error(`Could not load the plan catalog (${response.status})`);
  }

  const body = await response.json();
  const catalog = body?.data as PlanCatalog | undefined;

  // Fail loudly rather than rendering an empty price list, which would read as
  // "this product has no plans" to a prospect.
  if (!Array.isArray(catalog?.plans) || catalog.plans.length === 0) {
    throw new Error('The plan catalog response did not contain any plans');
  }

  return catalog;
}

/** The signed-in organization's plan, entitlements and trial state. */
export async function getSubscription(): Promise<Entitlements | null> {
  const response = await fetchAuth({ url: '/stripe/subscription', method: 'GET' });
  return response.ok ? (response.data.data as Entitlements) : null;
}

/** Usage meters and the current cost estimate. */
export async function getUsage(): Promise<UsageSummary | null> {
  const response = await fetchAuth({ url: '/stripe/usage', method: 'GET' });
  return response.ok ? (response.data.data as UsageSummary) : null;
}

/**
 * Start a hosted Stripe Checkout session.
 * @returns the URL to redirect the browser to
 */
export async function startCheckout(
  planId: PlanId,
  interval: BillingInterval,
  successUrl?: string,
  cancelUrl?: string
): Promise<{ ok: boolean; url?: string; message?: string }> {
  const response = await fetchAuth({
    url: '/stripe/checkout',
    method: 'POST',
    body: { planId, interval, successUrl, cancelUrl },
  });

  if (!response.ok) {
    return { ok: false, message: response.data?.message || 'Could not start checkout' };
  }

  return { ok: true, url: response.data.data.url };
}

/** Move an active subscription to a different plan or billing interval. */
export async function changePlan(
  planId: PlanId,
  interval?: BillingInterval
): Promise<{ ok: boolean; message: string }> {
  const response = await fetchAuth({
    url: '/stripe/subscription/change-plan',
    method: 'POST',
    body: { planId, interval },
  });

  return {
    ok: response.ok,
    message: response.data?.message || (response.ok ? 'Plan updated' : 'Could not change plan'),
  };
}

export async function cancelSubscription(
  immediately = false
): Promise<{ ok: boolean; message: string }> {
  const response = await fetchAuth({
    url: '/stripe/subscription/cancel',
    method: 'POST',
    body: { immediately },
  });

  return {
    ok: response.ok,
    message:
      response.data?.message || (response.ok ? 'Subscription cancelled' : 'Could not cancel'),
  };
}

export async function resumeSubscription(): Promise<{ ok: boolean; message: string }> {
  const response = await fetchAuth({ url: '/stripe/subscription/resume', method: 'POST' });

  return {
    ok: response.ok,
    message: response.data?.message || (response.ok ? 'Subscription resumed' : 'Could not resume'),
  };
}

/**
 * Open the Stripe-hosted billing portal, where cards, invoices, receipts and
 * tax details are managed. Keeping this hosted avoids handling card data.
 */
export async function openBillingPortal(
  returnUrl?: string
): Promise<{ ok: boolean; url?: string; message?: string }> {
  const response = await fetchAuth({
    url: '/stripe/billing-portal',
    method: 'POST',
    body: { returnUrl },
  });

  if (!response.ok) {
    return { ok: false, message: response.data?.message || 'Could not open the billing portal' };
  }

  return { ok: true, url: response.data.data.url };
}

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await fetchAuth({ url: '/stripe/payment-methods', method: 'GET' });
  return response.ok ? (response.data.data as PaymentMethod[]) : [];
}

export async function getInvoices(
  limit = 12
): Promise<{ invoices: Invoice[]; upcoming: Invoice | null }> {
  const response = await fetchAuth({ url: `/stripe/invoices?limit=${limit}`, method: 'GET' });
  return response.ok ? response.data.data : { invoices: [], upcoming: null };
}
